import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { notifAdmin } from 'services/apiService';
import SecureLS from 'secure-ls';

const AdminNotificationContext = createContext();

export const useAdminNotifications = () => {
  const context = useContext(AdminNotificationContext);
  if (!context) {
    throw new Error(
      'useAdminNotifications must be used within AdminNotificationProvider'
    );
  }
  return context;
};

export const AdminNotificationProvider = ({ children }) => {
  const [hasNewUserNotif, setHasNewUserNotif] = useState(false);
  const [hasNewTransactionNotif, setHasNewTransactionNotif] = useState(false);
  const [latestUserId, setLatestUserId] = useState(null);
  const [latestTransactionId, setLatestTransactionId] = useState(null);
  const ls = new SecureLS({ encodingType: 'aes' });
  const token = ls.get('token');
  const role = ls.get('role');
  const checkForNewNotifications = useCallback(async () => {
    if (role !== 'admin' || !token) return;

    try {
      const response = await notifAdmin({}, token);

      if (response.success) {
        const latestUser = response?.data?.latest_user;
        const latestTransaction = response?.data?.latest_transaction;

        let seenIds = ls.get('admin_seen_notif_ids');
        if (!seenIds || typeof seenIds !== 'object') {
          seenIds = { latestUserId: null, latestTransactionId: null };
        } else {
          // do nothing
        }
        [
          {
            type: 'user',
            data: latestUser,
            seenKey: 'latestUserId',
            setHasNew: setHasNewUserNotif,
            setLatest: setLatestUserId,
          },
          {
            type: 'transaction',
            data: latestTransaction,
            seenKey: 'latestTransactionId',
            setHasNew: setHasNewTransactionNotif,
            setLatest: setLatestTransactionId,
          },
        ].forEach(({ type, data, seenKey, setHasNew, setLatest }) => {
          switch (true) {
            case !!(data && data.id): {
              const isNew = seenIds[seenKey] !== data.id;
              setHasNew(isNew);
              setLatest(data.id);
              break;
            }
            default:
              setHasNew(false);
              break;
          }
        });
      }
    } catch (error) {
      console.error(
        '[Admin Notif Context] Error fetching notifications:',
        error
      );
    }
  }, [role, token, ls]);

  const markAsRead = useCallback(
    type => {
      try {
        let seenIds = ls.get('admin_seen_notif_ids');
        if (!seenIds || typeof seenIds !== 'object') {
          seenIds = { latestUserId: null, latestTransactionId: null };
        }

        switch (type) {
          case 'user':
            setHasNewUserNotif(false);
            if (latestUserId) {
              seenIds.latestUserId = latestUserId;
              ls.set('admin_seen_notif_ids', seenIds);
            }
            break;
          case 'transaction':
            setHasNewTransactionNotif(false);
            if (latestTransactionId) {
              seenIds.latestTransactionId = latestTransactionId;
              ls.set('admin_seen_notif_ids', seenIds);
            }
            break;
          case 'all':
            setHasNewUserNotif(false);
            setHasNewTransactionNotif(false);
            if (latestUserId) {
              seenIds.latestUserId = latestUserId;
            }
            if (latestTransactionId) {
              seenIds.latestTransactionId = latestTransactionId;
            }
            ls.set('admin_seen_notif_ids', seenIds);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('[Admin Notif Context] Error marking as read:', error);
      }
    },
    [ls, latestUserId, latestTransactionId]
  );

  // Fetch notifications setiap 3 detik
  useEffect(() => {
    if (role === 'admin' && token) {
      checkForNewNotifications();
      const interval = setInterval(() => {
        checkForNewNotifications();
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [role, token, checkForNewNotifications]);

  const value = {
    hasNewUserNotif,
    hasNewTransactionNotif,
    markAsRead,
    refreshNotifications: checkForNewNotifications,
  };

  return (
    <AdminNotificationContext.Provider value={value}>
      {children}
    </AdminNotificationContext.Provider>
  );
};
